import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { confirmDialog } from "@/common/components/ConfirmDialog";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSession } from "@/features/auth/hooks/useSession";
import { getActiveWorkspaceId } from "@/lib/api/workspace-context";
import { queryKeys } from "@/lib/query/keys";
import { getTrips, saveTrip, saveFavorite, submitTravelAnswer, type Trip, type Favorite } from "@/lib/api/endpoints/profile-interview";
import { InterviewPanel } from "@/features/interview/InterviewPanel";
import { resolveImageUrl } from "@/modules/places/resolveImageUrl";
import "./PeopleMap.css";

const MONTH_W = 150; // min px per month; drives how many fit / when to scroll

const TALK_SEEDS = [
  "Where are you headed? Tell me your trips with rough dates.",
  "What travel do you have planned — destinations and when?",
  "Tell me your upcoming trips: where, and what dates?",
  "What's on your travel radar? Give me places and dates.",
];
const pick = (a: string[]) => a[Math.floor(Math.random() * a.length)];

const MS_DAY = 86400000;
const parseISO = (s: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s || "");
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
};
const fmt = (d: Date) => d.toLocaleString("default", { month: "short", day: "numeric" });

interface TripEdit {
  id?: string;
  destination: string;
  start: string;
  end: string;
  stay: string;
  note: string;
}

/**
 * Travel tab — a clear forward timeline (next ~4 months). Today is marked, and
 * each trip is a block spanning its dates, even across month boundaries.
 */
export function TravelPanel() {
  const { token } = useSession();
  const ws = getActiveWorkspaceId();
  const queryClient = useQueryClient();
  const queryKey = queryKeys.synthesis.tab(ws, "travel");
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => getTrips(token ?? null, ws),
    enabled: Boolean(token),
    staleTime: 5 * 60 * 1000,
  });
  const trips: Trip[] = data?.trips ?? [];
  const favorites: Favorite[] = data?.favorites ?? [];
  const [seed, setSeed] = useState<string | null>(null);
  const [edit, setEdit] = useState<TripEdit | null>(null);
  const [favEdit, setFavEdit] = useState<{ id?: string; place: string; name: string; note: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [wrapEl, setWrapEl] = useState<HTMLDivElement | null>(null);
  const [wrapW, setWrapW] = useState(0);
  const [zoomMonths, setZoomMonths] = useState<number | null>(null); // null = auto-fit

  useEffect(() => {
    if (!wrapEl) return;
    const ro = new ResizeObserver(([e]) => setWrapW(e.contentRect.width));
    ro.observe(wrapEl);
    setWrapW(wrapEl.clientWidth);
    return () => ro.disconnect();
  }, [wrapEl]);
  // Auto-fit by width; the zoom slider overrides (and scrolls if it overflows).
  const autoMonths = Math.max(4, Math.min(12, Math.floor((wrapW || 720) / MONTH_W)));
  const MONTHS = zoomMonths ?? autoMonths;
  const trackW = Math.max(wrapW, MONTHS * MONTH_W); // px width of the scrolling track

  const persist = async (input: Parameters<typeof saveTrip>[2]) => {
    setSaving(true);
    try {
      await saveTrip(token ?? null, ws, input);
      setEdit(null);
      await queryClient.invalidateQueries({ queryKey });
    } finally {
      setSaving(false);
    }
  };
  const persistFav = async (input: Parameters<typeof saveFavorite>[2]) => {
    setSaving(true);
    try {
      await saveFavorite(token ?? null, ws, input);
      setFavEdit(null);
      await queryClient.invalidateQueries({ queryKey });
    } finally {
      setSaving(false);
    }
  };

  // Timeline window: start of this month → +MONTHS.
  const now = new Date();
  const axisStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const axisEnd = new Date(now.getFullYear(), now.getMonth() + MONTHS, 1);
  const span = (axisEnd.getTime() - axisStart.getTime()) / MS_DAY;
  const pct = (d: Date) => ((d.getTime() - axisStart.getTime()) / MS_DAY / span) * 100;

  // Month boundary markers within the window.
  const monthMarks = Array.from({ length: MONTHS }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    return { left: pct(d), label: d.toLocaleString("default", { month: "short" }), year: d.getFullYear() };
  });

  // Trips that intersect the window, with positions.
  const placed = trips
    .map((t) => {
      const s = parseISO(t.start);
      const e = parseISO(t.end) ?? s;
      if (!s || !e) return null;
      const end = new Date(e.getTime() + MS_DAY); // inclusive end
      if (end <= axisStart || s >= axisEnd) return { t, off: true as const };
      const left = Math.max(pct(s), 0);
      const right = Math.min(pct(end), 100);
      return { t, off: false as const, left, width: Math.max(right - left, 1.5), s, e };
    })
    .filter(Boolean) as Array<{ t: Trip; off: boolean; left?: number; width?: number; s?: Date; e?: Date }>;
  const inWindow = placed.filter((p) => !p.off);
  const offWindow = placed.filter((p) => p.off);
  const laid = layout(inWindow);
  const laneCount = Math.max(1, ...laid.map((x) => x.lane + 1));
  // Stable per-trip colors — a single blue ramp (synthesis token family) so
  // adjacent trips stay distinguishable without breaking the module's palette.
  const COLORS = ["var(--syn-accent, #15a5e3)", "var(--syn-accent-deep, #0c7bb3)", "var(--syn-accent-mid, #3b82f6)", "var(--syn-accent-light, #7cc7ea)"];
  const colorOf = (id: string) => COLORS[trips.findIndex((t) => t.id === id) % COLORS.length] ?? COLORS[0];

  return (
    <div className="people-map">
      <div className="people-map-bar">
        <button className="people-add-btn people-talk-btn" onClick={() => setSeed(pick(TALK_SEEDS))}>
          💬 Talk to Xavior about travel
        </button>
        <label className="tl-zoom" title="How many months to show">
          <span>🔍</span>
          <input type="range" min={3} max={12} value={MONTHS} onChange={(e) => setZoomMonths(+e.target.value)} />
          <span className="tl-zoom-val">{MONTHS} mo</span>
        </label>
      </div>

      {isLoading ? (
        <div className="people-map-loading">Loading…</div>
      ) : (
        <div className="tl">
          {/* Scrolling timeline track — more months when there's room, scroll when tight */}
          <div className="tl-scroll" ref={setWrapEl}>
            <div className="tl-track" style={{ width: `${trackW}px` }}>
              <div className="tl-months">
                {monthMarks.map((m, i) => (
                  <span key={i} className="tl-month" style={{ left: `${m.left}%` }}>
                    {m.label}
                  </span>
                ))}
              </div>
              <div className="tl-axis" style={{ minHeight: `${24 + laneCount * 46}px` }}>
                {monthMarks.map((m, i) => (
                  <div key={i} className="tl-gridline" style={{ left: `${m.left}%` }} />
                ))}
                <div className="tl-today" style={{ left: `${pct(now)}%` }} title="Today" />
                {laid.map(({ p, lane }) => {
                  const color = colorOf(p.t.id);
                  return (
                    <button
                      key={p.t.id}
                      className="tl-event"
                      style={{ left: `${p.left}%`, width: `${p.width}%`, top: `${10 + lane * 46}px` }}
                      title={`${[p.t.destination, p.t.stay].filter(Boolean).join(" · ")} · ${p.s ? fmt(p.s) : ""}–${p.e ? fmt(p.e) : ""}`}
                      onClick={() => setEdit({ id: p.t.id, destination: p.t.destination, start: p.t.start, end: p.t.end, stay: p.t.stay, note: p.t.note })}
                    >
                      <span className="tl-event-bar" style={{ background: color }} />
                      <span className="tl-event-label">
                        <span className="tl-event-dest" style={{ color }}>{p.t.destination}</span>
                        <span className="tl-event-dates">{p.s ? fmt(p.s) : ""}–{p.e ? fmt(p.e) : ""}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {offWindow.length > 0 && (
            <div className="tl-other">
              <span className="tl-other-label">Beyond this window:</span>
              {offWindow.map((p) => (
                <button key={p.t.id} className="work-task tl-other-chip" onClick={() => setEdit({ id: p.t.id, destination: p.t.destination, start: p.t.start, end: p.t.end, stay: p.t.stay, note: p.t.note })}>
                  {[p.t.destination, p.t.stay].filter(Boolean).join(" · ")} {p.s ? `(${fmt(p.s)})` : ""}
                </button>
              ))}
            </div>
          )}
          {trips.length === 0 && (
            <div className="people-map-empty" style={{ marginTop: 12 }}>
              No trips yet — tell me where you're headed with dates ("Tokyo June 26–July 3, staying at the Park Hyatt").
            </div>
          )}

          {/* Favorite places to stay */}
          <div className="tl-favs">
            <div className="tl-favs-head">
              <span className="tl-favs-title">Favorite stays</span>
              <button className="tl-fav-add" onClick={() => setFavEdit({ place: "", name: "", note: "" })}>＋ Add</button>
            </div>
            {favorites.length === 0 ? (
              <div className="tl-favs-empty">None yet — tell me where you love to stay and I'll remember it.</div>
            ) : (
              <div className="tl-favs-list">
                {favorites.map((f) => {
                  const photo = resolveImageUrl(f.photoUrl);
                  return (
                  <button key={f.id} className="tl-fav" onClick={() => setFavEdit({ id: f.id, place: f.place, name: f.name, note: f.note })}>
                    {photo ? (
                      <img
                        className="tl-fav-photo"
                        src={photo}
                        alt=""
                        loading="lazy"
                        // A broken/truncated photo degrades to the ★ placeholder
                        // instead of a broken-image icon.
                        onError={(e) => {
                          const img = e.currentTarget;
                          const star = img.nextElementSibling as HTMLElement | null;
                          img.style.display = "none";
                          if (star) star.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <span
                      className="tl-fav-photo tl-fav-photo-empty"
                      style={{ display: photo ? "none" : "flex" }}
                    >
                      ★
                    </span>
                    <span className="tl-fav-text">
                      <span className="tl-fav-name">{f.name}</span>
                      {f.place && <span className="tl-fav-place">{f.place}</span>}
                    </span>
                  </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {seed && (
        <InterviewPanel
          seedQuestion={seed}
          title="Talk to Xavior about travel"
          onAnswer={submitTravelAnswer}
          onClose={() => setSeed(null)}
          onComplete={() => {
            void queryClient.invalidateQueries({ queryKey });
          }}
        />
      )}

      {edit &&
        createPortal(
          <div className="people-edit-overlay" onMouseDown={() => setEdit(null)}>
            <div className="people-edit" onMouseDown={(e) => e.stopPropagation()}>
              <div className="people-edit-title">{edit.id ? "Edit trip" : "Add trip"}</div>
              <label className="people-edit-field">
                <span>Destination</span>
                <input value={edit.destination} autoFocus onChange={(e) => setEdit({ ...edit, destination: e.target.value })} />
              </label>
              <label className="people-edit-field">
                <span>Start</span>
                <input type="date" value={edit.start} onChange={(e) => setEdit({ ...edit, start: e.target.value })} />
              </label>
              <label className="people-edit-field">
                <span>End</span>
                <input type="date" value={edit.end} onChange={(e) => setEdit({ ...edit, end: e.target.value })} />
              </label>
              <label className="people-edit-field">
                <span>Staying at</span>
                <input value={edit.stay} placeholder="hotel / where you're staying" onChange={(e) => setEdit({ ...edit, stay: e.target.value })} />
              </label>
              <label className="people-edit-field">
                <span>Notes</span>
                <textarea rows={2} value={edit.note} onChange={(e) => setEdit({ ...edit, note: e.target.value })} />
              </label>
              <div className="people-edit-actions">
                {edit.id && (
                  <button className="people-edit-remove" disabled={saving} onClick={async () => { if (await confirmDialog({ message: "Delete this trip?", confirmLabel: "Delete", danger: true })) persist({ id: edit.id, remove: true }); }}>
                    Delete
                  </button>
                )}
                <button className="people-edit-cancel" onClick={() => setEdit(null)} disabled={saving}>Cancel</button>
                <button
                  className="people-edit-save"
                  disabled={saving || !edit.destination.trim()}
                  onClick={() => persist({ id: edit.id, destination: edit.destination.trim(), start: edit.start, end: edit.end || edit.start, stay: edit.stay, note: edit.note })}
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {favEdit &&
        createPortal(
          <div className="people-edit-overlay" onMouseDown={() => setFavEdit(null)}>
            <div className="people-edit" onMouseDown={(e) => e.stopPropagation()}>
              <div className="people-edit-title">{favEdit.id ? "Edit favorite" : "Add favorite stay"}</div>
              <label className="people-edit-field">
                <span>Name</span>
                <input value={favEdit.name} autoFocus placeholder="hotel / property" onChange={(e) => setFavEdit({ ...favEdit, name: e.target.value })} />
              </label>
              <label className="people-edit-field">
                <span>Place</span>
                <input value={favEdit.place} placeholder="city / area" onChange={(e) => setFavEdit({ ...favEdit, place: e.target.value })} />
              </label>
              <label className="people-edit-field">
                <span>Why you like it</span>
                <textarea rows={2} value={favEdit.note} onChange={(e) => setFavEdit({ ...favEdit, note: e.target.value })} />
              </label>
              <div className="people-edit-actions">
                {favEdit.id && (
                  <button className="people-edit-remove" disabled={saving} onClick={async () => { if (await confirmDialog({ message: "Remove this favorite?", confirmLabel: "Remove", danger: true })) persistFav({ id: favEdit.id, remove: true }); }}>
                    Remove
                  </button>
                )}
                <button className="people-edit-cancel" onClick={() => setFavEdit(null)} disabled={saving}>Cancel</button>
                <button className="people-edit-save" disabled={saving || !favEdit.name.trim()} onClick={() => persistFav({ id: favEdit.id, name: favEdit.name.trim(), place: favEdit.place, note: favEdit.note })}>
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

// Greedy lane assignment so overlapping trip blocks stack instead of colliding.
function layout(items: Array<{ t: Trip; left?: number; width?: number; s?: Date; e?: Date }>) {
  const sorted = [...items].sort((a, b) => (a.left ?? 0) - (b.left ?? 0));
  const laneEnds: number[] = [];
  return sorted.map((p) => {
    const l = p.left ?? 0;
    const r = l + (p.width ?? 0);
    let lane = laneEnds.findIndex((end) => l >= end - 0.01);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(r);
    } else {
      laneEnds[lane] = r;
    }
    return { p, lane };
  });
}

export default TravelPanel;
